import React, {useState} from 'react';
import Form from "../shared/form/form";
import {H2, H5, H6} from "../shared/fonts/fonts";
import styled from "styled-components";
import {pageProps} from "../../pages/appointment";
import {ButtonPU, ButtonText} from "../shared/buttons/buttons";
import Error from "../auth/Error";
import InfoRow from "./InfoRow";
import Loading from "../shared/form/loading";
import {communications, DocumentPath, URLs, weekDays} from "../../utils/constants";
import {setWraps, toLocaleStringByMoscow} from "../../utils/functions";
import {CheckboxBlock} from "../shared/form/checkbox";
import Link from "../shared/form/link";
import {InputTextArea} from "../shared/form/input";
import {ByMoscowTime} from "./Main";
import Slot from "../../entities/Slot";
import {appoint} from "../../api/methods/slots/update/appoint";
import {book} from "../../api/methods/slots/update/book";

export const Fields = styled.div`
  display: grid;
  grid-gap: 20px;
  width: 100%;
  
  .mobile & textarea {
    width: 100%;
  }
`;

export const Buttons = styled.div`
  display: grid;
  grid-gap: 20px;
  justify-items: center;
`;

interface ConfirmationProps extends pageProps {
    slot: Slot;
    payment_url: string | null;
}

export const dateToString = (ts: number) => {
    const d_ = new Date(ts * 1000);
    const s_ = toLocaleStringByMoscow(d_);
    return s_.slice(0, 10) + ', ' + weekDays[(d_.getDay() + 6) % 7];
}

export const timeToString = (ts: number) => {
    const d_ = new Date(ts * 1000);
    const s_ = toLocaleStringByMoscow(d_);
    return s_.slice(12, 17);
}

const DateStyle = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  grid-gap: 8px;
  align-items: center;
  
  & :nth-child(2) {
    opacity: 0.5;
  }
`;

export const DateBlock = (props: {slot: Slot}) =>
    <DateStyle>
        <H5>
            {timeToString(props.slot.timestamp)}
        </H5>
        <span>-</span>
        <H5>
            {timeToString(props.slot.timestamp + props.slot.consultation.duration * 60)}
        </H5>
        <H5><ByMoscowTime>по <span>московскому</span> времени</ByMoscowTime></H5>
    </DateStyle>;

export const Links = styled.div`
  display: grid;
`;

export const CommunicationStyle = styled.div`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  justify-content: start;
  grid-gap: 10px;
  margin: 5px 0;
  
  & img {
    border-radius: 20%;
  }
`;

export const Communication = (props: {comm_link: string}) => {
    const communication = communications.find(c_ => c_.link === props.comm_link);

    return (<CommunicationStyle>
        <img src={communication?.icon} />
        <H5>{communication?.title}</H5>
    </CommunicationStyle>)
};

const Confirmation = (props: ConfirmationProps) => {
    const [error, setError] = useState<string|null>(null);
    const [check, setCheck] = useState(false);
    const [communicationValue, setCommunicationValue_] = useState('');
    const setCommunicationValue = (e: any) => setCommunicationValue_(e.target.value.slice(0, 255));

    const next = async () => {
        try {
            if (props.payment_url) {
                await book(props.slot, communicationValue);
                window.open(props.payment_url, '_self');
                return;
            }
            await appoint(props.slot, communicationValue);
            window.open(URLs.appointmentResult + '?id=' + props.slot.id, '_self');
        } catch (error: any) {
            if (error.response && error.response.data && error.response.data.text) {
                setError(error.response.data.text);
            }
            else {
                setError('Неизвестная ошибка. Попробуйте позже.');
            }
        }
    }

    const prev = () => props.setPage('login', {});

    if (!props.slot) {
        return (<Form>
            <H2>Запись на онлайн-информирование</H2>
            <Fields>
                <Loading />
            </Fields>
            <Buttons>
                <ButtonText onClick={prev}>
                    <H6>Вернуться</H6>
                </ButtonText>
            </Buttons>
        </Form>);
    }

    return (
        <Form>
            <H2>Запись на онлайн-информирование</H2>
            <Fields>
                <InfoRow header={'Название:'}><H5>{props.slot.consultation.title}</H5></InfoRow>
                {
                    props.slot.consultation.description &&
                    <InfoRow header={'Описание:'}><H5>{setWraps(props.slot.consultation.description)}</H5></InfoRow>
                }
                <InfoRow header={'Длительность:'}><H5>{props.slot.consultation.duration} мин.</H5></InfoRow>
                <InfoRow header={'Стоимость:'}><H5>{props.slot.consultation.price} руб.</H5></InfoRow>
                <InfoRow header={'Время:'}>
                    <H5>
                        {dateToString(props.slot.timestamp)}
                    </H5>
                    <DateBlock slot={props.slot} />
                </InfoRow>
                <InfoRow header={'Средства связи:'}>
                    {props.slot.consultation.communications.map((c, i) => <Communication key={i} comm_link={c} />)}
                </InfoRow>
                <InfoRow header={'Контакты для связи:'} required={true}>
                <InputTextArea status={communicationValue ? 'success' : ''}
                           input={{value: communicationValue, onInput: setCommunicationValue, name: 'comm_value',
                               placeholder: 'Укажите контакты для связи хотя бы одним из указанных выше способов'}} />
                </InfoRow>
                <InfoRow header={'Я ознакомлен и согласен со следующими документами:'}>
                    <CheckboxBlock checkbox={{value: check, onChange: () => setCheck(!check)}}>
                        <Links>
                            <Link target={'_blank'} href={DocumentPath.oferta}>
                                Оферта
                            </Link>
                            <Link target={'_blank'} href={DocumentPath.conf_policy}>
                                Политика конфиденциальности
                            </Link>
                            <Link target={'_blank'} href={DocumentPath.pd_policy}>
                                Политика обработки персональных данных
                            </Link>
                            <Link target={'_blank'} href={DocumentPath.user_agreement}>
                                Пользовательское соглашение
                            </Link>
                            <Link target={'_blank'} href={DocumentPath.pd_agreement}>
                                Согласие на обработку персональных данных
                            </Link>
                        </Links>
                    </CheckboxBlock>
                </InfoRow>
            </Fields>
            <Buttons>
                {
                    error && <Error>{error}</Error>
                }
                <ButtonPU onClick={next} className={check && communicationValue ? '' : 'disabled'}>
                    {
                        props.payment_url && <H5>Перейти к оплате</H5>
                    }
                    {
                        !props.payment_url && <H5>Записаться</H5>
                    }
                </ButtonPU>
                <ButtonText onClick={prev}>
                    <H6>Вернуться</H6>
                </ButtonText>
            </Buttons>
        </Form>
    );
};

export default Confirmation;
